import { Button, Modal, Box, TextField, InputAdornment, Fade, Backdrop } from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  footerButtonWidth,
  cancelButtonStyles,
  registerPatientButtonStyles,
} from "../../common/commonStyles";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";

// ---- shared palette (matches PatientRegistrationForm / BranchCreationForm) ----
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  tealSoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  coral: "#FF6B57",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    fontSize: "14px",
    "& fieldset": { borderColor: palette.line },
    "&:hover fieldset": { borderColor: palette.teal },
    "&.Mui-focused fieldset": { borderColor: palette.teal, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    "&.Mui-focused": { color: palette.teal },
  },
  "& .MuiInputAdornment-root svg": {
    fontSize: "19px",
    color: palette.slate,
  },
};

const SectionLabel = ({ icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "14px" }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: palette.tealSoft,
        color: palette.teal,
        "& svg": { fontSize: "16px" },
      }}
    >
      {icon}
    </Box>
    <span
      style={{
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: palette.slate,
      }}
    >
      {children}
    </span>
  </Box>
);

const ServiceCreationForm = ({
  open,
  handleClose,
  formData,
  handleInputChange,
  handleCreateService,
  isEditClicked,
  validationErrors,
}) => {
  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300, sx: { backgroundColor: "rgba(17, 24, 39, 0.55)" } } }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "760px",
            maxWidth: "94vw",
            maxHeight: "88vh",
            overflowY: "auto",
            bgcolor: palette.bg,
            borderRadius: "18px",
            boxShadow: "0 24px 60px -12px rgba(15, 23, 42, 0.35)",
            outline: "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "22px 28px",
              background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.tealDark} 100%)`,
              borderRadius: "18px 18px 0 0",
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                backgroundColor: "rgba(255,255,255,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DesignServicesRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <h3 style={{ margin: 0, color: "#fff", fontSize: "18px", fontWeight: 700 }}>
                {isEditClicked ? "Edit Service" : "Create a New Service"}
              </h3>
              <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.75)", fontSize: "12.5px" }}>
                {isEditClicked
                  ? "Update the service's details below"
                  : "Define the service, its fees, and session duration"}
              </p>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ padding: "26px 28px 28px" }}>
            {/* Service details */}
            <SectionLabel icon={<DesignServicesRoundedIcon />}>Service Details</SectionLabel>
            <Grid container spacing={2} sx={{ mb: "26px" }}>
              <Grid size={5}>
                <TextField
                  fullWidth
                  label="Name"
                  size="small"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DesignServicesRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={7}>
                <TextField
                  fullWidth
                  label="Description"
                  size="small"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.description}
                  helperText={validationErrors.description}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Pricing & duration */}
            <SectionLabel icon={<PaymentsRoundedIcon />}>Pricing &amp; Duration</SectionLabel>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Consultation Fee"
                  size="small"
                  name="consultation_fee"
                  value={formData.consultation_fee}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.consultation_fee}
                  helperText={validationErrors.consultation_fee}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PaymentsRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Therapy Session Fee"
                  size="small"
                  name="therapy_session_fee"
                  value={formData.therapy_session_fee}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.therapy_session_fee}
                  helperText={validationErrors.therapy_session_fee}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalHospitalRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Therapy Duration (min)"
                  size="small"
                  name="therapy_duration_minutes"
                  value={formData.therapy_duration_minutes}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.therapy_duration_minutes}
                  helperText={validationErrors.therapy_duration_minutes}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TimerRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                mt: "30px",
                pt: "20px",
                borderTop: `1px solid ${palette.line}`,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  borderColor: palette.line,
                  color: palette.slate,
                  padding: "8px 22px",
                  "&:hover": { borderColor: palette.slate, backgroundColor: "#F3F4F6" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateService}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  padding: "8px 24px",
                  backgroundColor: palette.teal,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: palette.tealDark, boxShadow: "none" },
                }}
              >
                {isEditClicked ? "Save Changes" : "Create Service"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default ServiceCreationForm;
